import { Content, ContentStack, ContentTable } from "pdfmake/interfaces";
import HeaderSapiensForm from "react-lib/apps/HISV3/DPO/sequence/HeaderSapiensForm";
import {
  emptyHospitalContent,
  HospitalAddressMap,
  OADetailType,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

import CONFIG from "config/config";
import { CompanyType } from "../../HISV3/common/CompanyLogoForm";

const color = {
  greyHeader: "#D9D9D9",
  greySummary: "#E8E8E8",
  greyBorder: "#9f9f9f",
  pink: "#F382A0",
  blue: "#095e9d",
};

export type FormPaymentSummaryType = {
  // data
  fields?: Record<string, any>[];
  oaDetail?: OADetailType;
  totalSum?: string;
  discountSum?: string;
  paidSum?: string;
  grandSum?: string;
  unpaidSum?: string;
  hn?: string;
  patientNameTH?: string;
  patientNameEN?: string;
  visitDate?: string;
  encounter?: string;
  printedDate?: string;
  printedBy?: string;
  division?: string;
};

const extractHn = (hn: string): string => {
  if (!hn) return "";

  const parts = hn.split("_");

  return parts[parts.length - 1];
};

const FormPaymentSummary = async (props: FormPaymentSummaryType) => {
  var origin = window?.location?.origin;

  const getHospitalAddress = (company: CompanyType, oaDetail?: OADetailType): Content => {
    return HospitalAddressMap[company]?.(oaDetail, props?.division) ?? emptyHospitalContent;
  };

  const headerSapiensForm = await HeaderSapiensForm({
    form: "FormPaymentSummary",
    images: {
      oaLogo: props.oaDetail?.imageFile || "",
    },
    logoHight:
      CONFIG.COMPANY === "MSK"
        ? 40
        : CONFIG.COMPANY === "RAMASRI"
          ? 45
          : CONFIG.COMPANY === "MEHEALTH"
            ? 45
            : 24,
    pageMargins: [0, 178, 0, 193],
    hospitalAddress: getHospitalAddress(CONFIG.COMPANY as CompanyType, props.oaDetail),
    header: {
      margin: [-8, 0, 0, 0],
      stack: headerPaymentSummary(props),
    },
    styles: {
      paymentSummaryHeader: {
        bold: true,
        fontSize: 8,
      },
      paymentSummaryInfo: {
        fontSize: 8,
      },
    },
  });

  const fields = props.fields ?? [];

  const splitText = (text: any, maxLength = 60) => {
    const value = String(text ?? "");

    const result = [];

    for (let i = 0; i < value.length; i += maxLength) {
      result.push(value.slice(i, i + maxLength));
    }

    return result.slice(0, 3);
  };

  const fileData = fields.flatMap((item: any) => {
    const name = CONFIG.COMPANY === "MEHEALTH" ? item?.pricing_name : item?.display_name;

    const nameLines = splitText(name, 60);

    return nameLines.map((line, i) => {
      return [
        {
          text: i === 0 ? (CONFIG.COMPANY === "MEHEALTH" ? item?.pricing_code : item?.code) : "",
          font: "KanitLM",
          style: "tableData",
          alignment: "center",
          margin: [0, 0, 3, 0],
          border: [true, false, true, false],
        },
        {
          text: line,
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 3, 0],
          border: [true, false, true, false],
        },
        {
          text: i === 0 ? item?.quantity || "" : "",
          font: "KanitLM",
          style: "tableData",
          alignment: "center",
          margin: [0, 0, 3, 0],
          border: [true, false, true, false],
        },
        {
          text: i === 0 ? item?.unitprice || "" : "",
          font: "KanitLM",
          style: "tableData",
          alignment: "right",
          margin: [0, 0, 3, 0],
          border: [true, false, true, false],
        },
        {
          text: i === 0 ? item?.total || "" : "",
          font: "KanitLM",
          style: "tableData",
          alignment: "right",
          margin: [0, 0, 3, 0],
          border: [true, false, true, false],
        },
        {
          text: i === 0 ? item?.paid || "" : "",
          font: "KanitLM",
          style: "tableData",
          alignment: "right",
          margin: [0, 0, 3, 0],
          border: [true, false, true, false],
        },
      ];
    });
  });

  const totalUsedRows = fields.reduce((total, item) => {
    const name = CONFIG.COMPANY === "MEHEALTH" ? item?.pricing_name : item?.display_name;

    return total + splitText(name, 60).length;
  }, 0);

  const maxRows = 22
  const remainder = totalUsedRows % maxRows;
  const numberRows = remainder === 0 ? 0 : maxRows - remainder;

  const emptyCell = () => ({
    text: " ",
    font: "KanitLM",
    style: "tableData",
    margin: [0, 0, 3, 0],
    border: [true, false, true, false],
  });

  const rowEmpty = Array.from({ length: numberRows || maxRows }).map(() => [
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
  ]);

  const tableRowHeight = 15;

  return {
    pageSize: "A4",
    ...headerSapiensForm,
    content: [
      {
        margin: [15, 0, 15, 0],
        stack: [
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [55, "*", 40, 60, 60, 60],
              heights: () => tableRowHeight,
              body: [
                [
                  {
                    stack: [
                      {
                        text: "รหัส",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Code",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [true, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "รายการ",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Detail",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "จำนวน",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Qty",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ราคา/หน่วย",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Price/Unit",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ราคารวม",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Total",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ชำระแล้ว",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Paid",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, true, false],
                  },
                ],
                ...fileData,
                ...rowEmpty,
              ],
            },
            layout: layoutTable,
          },
        ],
      },
    ],
    styles: {
      ...headerSapiensForm.styles,
      tableData: {
        fontSize: 8,
        lineHeight: 1,
      },
      tableDataPoppins: { fontSize: 6 },
      tableHeaderSummary: {
        bold: true,
        fontSize: 8,
      },
      footerSignature: { fontSize: 9 },
      footerData: { bold: true, fontSize: 6 },
    },
    footer: function (currentPage: number, pageCount: number) {
      return {
        margin: [15, 0, 15, 0],
        stack: [
          {
            table: {
              headerRows: 1,
              widths: [55, "*", 40, 60, 60, 60],
              body: [
                [
                  {
                    text: thEn("ยอดรวมเงิน ", "Subtotal"),
                    margin: [10, 5, 0, 5],
                    border: [true, true, false, remainder + 1 === 28],
                    colSpan: 4,
                  },
                  {},
                  {},
                  {},
                  {
                    text: props.totalSum || "0.00",
                    font: "Poppins",
                    style: "tableHeaderSummary",
                    alignment: "right",
                    margin: [0, 5, 3, 5],
                    border: [false, true, false, remainder + 1 === 28],
                  },
                  {
                    text: "",
                    border: [false, true, true, remainder + 1 === 28],
                  },
                ],

                // Discount
                [
                  {
                    text: thEn("ส่วนลด ", "Discount"),
                    margin: [10, 0, 0, 5],
                    border: [true, false, false, remainder + 2 === 28],
                    colSpan: 4,
                  },
                  {},
                  {},
                  {},
                  {
                    text: props.discountSum || "0.00",
                    font: "Poppins",
                    style: "tableHeaderSummary",
                    alignment: "right",
                    margin: [0, 0, 3, 0],
                    border: [false, false, false, remainder + 2 === 28],
                  },
                  {
                    text: "",
                    border: [false, false, true, remainder + 2 === 28],
                  },
                ],

                // Grand Total
                [
                  {
                    text: thEn("ยอดรวมสุทธิ ", "Grand Total"),
                    margin: [10, 0, 0, 5],
                    fillColor: color.greySummary,
                    border: [true, false, false, remainder + 3 === 28],
                    colSpan: 4,
                  },
                  {},
                  {},
                  {},
                  {
                    text: props.grandSum || "0.00",
                    font: "Poppins",
                    style: "tableHeaderSummary",
                    alignment: "right",
                    margin: [0, 0, 3, 0],
                    fillColor: color.greySummary,
                    border: [false, false, false, remainder + 3 === 28],
                  },
                  {
                    text: "",
                    fillColor: color.greySummary,
                    border: [false, false, true, remainder + 3 === 28],
                  },
                ],

                // Paid
                [
                  {
                    text: thEn("ชำระแล้ว ", "Paid"),
                    margin: [10, 0, 0, 5],
                    border: [true, false, false, remainder + 4 === 28],
                    colSpan: 4,
                  },
                  {},
                  {},
                  {},
                  {
                    text: props.paidSum || "0.00",
                    font: "Poppins",
                    style: "tableHeaderSummary",
                    alignment: "right",
                    margin: [0, 0, 3, 0],
                    border: [false, false, false, remainder + 4 === 28],
                  },
                  {
                    text: "",
                    border: [false, false, true, remainder + 4 === 28],
                  },
                ],

                // Outstanding
                [
                  {
                    text: thEn("ค้างชำระ ", "Outstanding Balance"),
                    margin: [10, 0, 0, 5],
                    border: [true, false, false, true],
                    colSpan: 4,
                  },
                  {},
                  {},
                  {},
                  {
                    text: props.unpaidSum || "0.00",
                    font: "Poppins",
                    style: "tableHeaderSummary",
                    alignment: "right",
                    margin: [0, 0, 3, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: "",
                    border: [false, false, true, true],
                  },
                ],
              ],
            },
            layout: layoutTable,
          },

          {
            margin: [15, 50, 15, 10],
            stack: footerPaymentSummary(props),
          },
        ],
      };
    },
    images: {
      ...headerSapiensForm.images,
      background: origin + "/static/images/logoform/SAPIENS/HeaderSapiensForm.png",
    },
  };
};

export default FormPaymentSummary;

/*================================ HANDLES RECEIPT ==============================*/

const headerPaymentSummary = (props: FormPaymentSummaryType) => {
  return [
    {
      margin: [0, 0, 0, 15],
      stack: [
        {
          text: [
            {
              text: "รายละเอียดค่าใช้จ่าย",
              font: "KanitLM",
              style: {
                bold: true,
                fontSize: 10,
              },
            },
            {
              text: "(Service Item Details)",
              font: "PoppinsLM",
              style: {
                bold: true,
                fontSize: 10,
              },
            },
          ],
          alignment: "center",
          border: [false, false, false, false],
        },
      ],
    },
    {
      table: {
        widths: [120, 130, 105, "*"],
        body: [
          [
            headerLabelCell("หมายเลขผู้รับบริการ ", "/ ID :"),
            headerText([textItem(extractHn(props.hn || ""), { font: "PoppinsLM" })]),
            headerLabelCell("ชื่อ ", "/ Name :"),
            headerText([textItem(`${props.patientNameTH} (${props.patientNameEN || ""})`)]),
          ],
          [
            headerLabelCell("วันที่รับบริการ ", "/ Visit Date :"),
            headerText([textItem(props.visitDate || "")]),
            headerLabelCell("หมายเลขรับบริการ ", "/ VN :"),
            headerText([textItem(props.encounter || "")]),
          ],
        ],
      },
      margin: [0, 0, 15, 0],
    },
  ];
};

const headerLabelCell = (th: string, en: string) => ({
  text: [
    { text: th, font: "KanitLM", style: "paymentSummaryHeader" },
    { text: en, font: "PoppinsLM", style: "paymentSummaryHeader" },
  ],
  alignment: "left",
  border: [false, false, false, false],
  margin: [10, 0, 0, 0],
});

type HeaderValueType = "text" | "columns";

const headerValueCell = (data: any, type: HeaderValueType = "text") => ({
  alignment: "left",
  border: [false, false, false, false],
  margin: [0, 0, 0, 0],
  ...(type === "columns" ? { columns: data } : { text: data ?? "" }),
});

type TextItemOptions = {
  font?: string;
  style?: string;
  width?: string | number;
  margin?: number[];
};

const textItem = (text: string, options: TextItemOptions = {}) => ({
  text,
  font: options.font ?? "KanitLM",
  style: options.style ?? "paymentSummaryInfo",
  ...(options.width ? { width: options.width } : {}),
  ...(options.margin ? { margin: options.margin } : {}),
});

const headerText = (text: any[]) => headerValueCell(text, "text");

/*================================ HOSPITAL ADDRESS ==============================*/

const bilingualTable = (thStack: any[], enStack: any[]): Content => ({
  relativePosition: { x: -10, y: 0 },
  table: {
    body: [
      [
        {
          lineHeight: 1,
          alignment: "left",
          border: [false, false, true, false],
          stack: thStack,
        },
        {
          lineHeight: 1.05,
          alignment: "left",
          margin: [5, 0, 0, 0],
          border: [true, false, false, false],
          stack: enStack,
        },
      ],
    ],
  },
});

/*================================ CONTENT RECEIPT ==============================*/

/*================================ FOOTER ==============================*/

const layoutTable = {
  hLineColor: () => color.greyBorder,
  vLineColor: () => color.greyBorder,
};

const thEn = (
  th: string,
  en: string,
  fontTh = "KanitLM",
  fontEn = "PoppinsLM",
  style = "tableHeaderSummary",
) => [
  { text: th, font: fontTh, style },
  { text: `/${en}`, font: fontEn, style },
];

const footerPaymentSummary = (props: FormPaymentSummaryType) => {
  return [
    {
      margin: [60, 6, 0, 10],
      columns: [
        {
          text: thEn("วันเวลาที่พิมพ์ ", "Printed Date :"),
          margin: [10, 0, 0, 0],
          width: 130,
        },
        {
          text: props.printedDate,
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 3, 0],
          width: 130,
        },
        {
          text: thEn("ผู้พิมพ์ ", "Printed by :"),
          margin: [0, 0, 3, 0],
          width: 90,
        },
        {
          text: props.printedBy,
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 3, 0],
          width: "*",
        },
      ],
    },
  ];
};
